'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetProposalField } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { ProposalBackerRewardsPanel } from '@/components/launchpad/proposal-backer-rewards-panel';
import { ProposalForm } from '@/components/launchpad/proposal-form';
import { AnimatedButton } from '@/components/ui/animated-button';
import { EmptyState } from '@/components/ui/empty-state';
import { Coins01IconSolid } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { useCreationFeeParams } from '@/hooks/launchpad/use-creation-fee-params';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ProposalDisplayStatus = 'active' | 'needs_debate' | 'pending_review' | 'creating' | 'live' | 'rejected' | 'expired';
interface OutcomeItem {
  index: number;
  label: string;
  stakeAmount: string;
  percentage: number;
  backerCount: number;
}
interface UserStakePosition {
  outcomeIndex: number;
  amount: string;
  tokenPrice: string | null;
  tokensReceived: string | null;
}
interface ProposalDetailRightProps {
  proposalPubkey: string;
  proposalTitle: string;
  proposalStatus: ProposalDisplayStatus;
  outcomes: OutcomeItem[];
  userStake: {
    positions: UserStakePosition[];
    totalAmount: string;
  } | null;
  isAuthenticated: boolean;
  withdrawFeePercent: number | undefined;
  /** `MarketConfig.fee_bps_backing` — basis points. Drives the Fund-tab estimate. */
  feeBpsBacking: number | undefined;
  /** `MarketConfig.fee_bps_unbacking` — basis points. Drives the Withdraw-tab estimate. */
  feeBpsUnbacking: number | undefined;
  currentTotalBackedD6: string;
  creationThresholdD6: string | undefined;
  onConnectWallet: () => void;
  /** When true, renders forms without Panel wrapper (for use inside sheets). */
  bare?: boolean;
  /** The proposal went live and the page is handing off to its market. */
  launching?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProposalDetailRight(t0) {
  const $ = _c(70);
  const {
    proposalPubkey,
    proposalTitle,
    proposalStatus,
    outcomes,
    userStake,
    isAuthenticated,
    withdrawFeePercent,
    feeBpsBacking,
    feeBpsUnbacking,
    currentTotalBackedD6,
    creationThresholdD6,
    onConnectWallet,
    bare: t1,
    launching: t2
  } = t0;
  const bare = t1 === undefined ? false : t1;
  const launching = t2 === undefined ? false : t2;
  const t = useTranslations("launchpad.detail");
  const [amountInput, setAmountInput] = useState("");
  const hasPositions = userStake !== null && userStake.positions.length > 0;
  const viewerBackerShareBalanceD6 = userStake?.totalAmount ?? "0";
  const rewardsEligible = proposalStatus === "active" || proposalStatus === "needs_debate" || proposalStatus === "pending_review";
  let t3;
  if ($[0] !== amountInput || $[1] !== creationThresholdD6 || $[2] !== currentTotalBackedD6 || $[3] !== isAuthenticated || $[4] !== proposalPubkey || $[5] !== rewardsEligible || $[6] !== viewerBackerShareBalanceD6) {
    t3 = rewardsEligible ? <BackingPhaseRewardsPanel proposalPubkey={proposalPubkey} amountInput={amountInput} currentTotalBackedD6={currentTotalBackedD6} viewerBackerShareBalanceD6={viewerBackerShareBalanceD6} creationThresholdD6={creationThresholdD6} showBackingInput={!isAuthenticated} onAmountInputChange={setAmountInput} /> : null;
    $[0] = amountInput;
    $[1] = creationThresholdD6;
    $[2] = currentTotalBackedD6;
    $[3] = isAuthenticated;
    $[4] = proposalPubkey;
    $[5] = rewardsEligible;
    $[6] = viewerBackerShareBalanceD6;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const rewardsPanel = t3;
  let t4;
  bb0: {
    if (!isAuthenticated) {
      let t5;
      if ($[8] !== t) {
        t5 = t("connectWallet");
        $[8] = t;
        $[9] = t5;
      } else {
        t5 = $[9];
      }
      let t6;
      if ($[10] !== t5) {
        t6 = <span className="text-body text-foreground-secondary">{t5}</span>;
        $[10] = t5;
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      let t7;
      if ($[12] !== t) {
        t7 = t("connectWalletCta");
        $[12] = t;
        $[13] = t7;
      } else {
        t7 = $[13];
      }
      let t8;
      if ($[14] !== onConnectWallet || $[15] !== t7) {
        t8 = <AnimatedButton onClick={onConnectWallet} className="w-full">{t7}</AnimatedButton>;
        $[14] = onConnectWallet;
        $[15] = t7;
        $[16] = t8;
      } else {
        t8 = $[16];
      }
      let t9;
      if ($[17] !== t6 || $[18] !== t8) {
        t9 = <Panel><PanelContent className="flex flex-col items-center gap-4 text-center">{t6}{t8}</PanelContent></Panel>;
        $[17] = t6;
        $[18] = t8;
        $[19] = t9;
      } else {
        t9 = $[19];
      }
      t4 = t9;
      break bb0;
    }
    if (proposalStatus === "rejected" || proposalStatus === "expired") {
      if (hasPositions) {
        if (bare) {
          let t5;
          if ($[20] !== amountInput || $[21] !== feeBpsBacking || $[22] !== feeBpsUnbacking || $[23] !== outcomes || $[24] !== proposalPubkey || $[25] !== proposalTitle || $[26] !== userStake || $[27] !== withdrawFeePercent) {
            t5 = <ProposalForm proposalPubkey={proposalPubkey} proposalTitle={proposalTitle} outcomes={outcomes} userStake={userStake} withdrawFeePercent={withdrawFeePercent} feeBpsBacking={feeBpsBacking} feeBpsUnbacking={feeBpsUnbacking} bare={true} withdrawOnly={true} amountInput={amountInput} onAmountInputChange={setAmountInput} />;
            $[20] = amountInput;
            $[21] = feeBpsBacking;
            $[22] = feeBpsUnbacking;
            $[23] = outcomes;
            $[24] = proposalPubkey;
            $[25] = proposalTitle;
            $[26] = userStake;
            $[27] = withdrawFeePercent;
            $[28] = t5;
          } else {
            t5 = $[28];
          }
          t4 = t5;
          break bb0;
        }
        let t5;
        if ($[29] !== amountInput || $[30] !== feeBpsBacking || $[31] !== feeBpsUnbacking || $[32] !== outcomes || $[33] !== proposalPubkey || $[34] !== proposalTitle || $[35] !== userStake || $[36] !== withdrawFeePercent) {
          t5 = <Panel density="compact" className="overflow-hidden"><PanelContent><ProposalForm proposalPubkey={proposalPubkey} proposalTitle={proposalTitle} outcomes={outcomes} userStake={userStake} withdrawFeePercent={withdrawFeePercent} feeBpsBacking={feeBpsBacking} feeBpsUnbacking={feeBpsUnbacking} bare={true} withdrawOnly={true} amountInput={amountInput} onAmountInputChange={setAmountInput} /></PanelContent></Panel>;
          $[29] = amountInput;
          $[30] = feeBpsBacking;
          $[31] = feeBpsUnbacking;
          $[32] = outcomes;
          $[33] = proposalPubkey;
          $[34] = proposalTitle;
          $[35] = userStake;
          $[36] = withdrawFeePercent;
          $[37] = t5;
        } else {
          t5 = $[37];
        }
        t4 = t5;
        break bb0;
      }
      let t5;
      if ($[38] !== t) {
        t5 = t("noBackingYet");
        $[38] = t;
        $[39] = t5;
      } else {
        t5 = $[39];
      }
      let t6;
      if ($[40] !== t5) {
        t6 = <EmptyState framed={true} icon={Coins01IconSolid} title={t5} />;
        $[40] = t5;
        $[41] = t6;
      } else {
        t6 = $[41];
      }
      t4 = t6;
      break bb0;
    }
    if (proposalStatus === "creating") {
      let t5;
      if ($[42] !== t) {
        t5 = t("marketCreating");
        $[42] = t;
        $[43] = t5;
      } else {
        t5 = $[43];
      }
      let t6;
      if ($[44] !== t5) {
        t6 = <Panel><PanelContent className="flex flex-col items-center gap-4 text-center"><span className="text-body text-foreground-secondary">{t5}</span></PanelContent></Panel>;
        $[44] = t5;
        $[45] = t6;
      } else {
        t6 = $[45];
      }
      t4 = t6;
      break bb0;
    }
    if (bare) {
      let t5;
      if ($[46] !== amountInput || $[47] !== feeBpsBacking || $[48] !== feeBpsUnbacking || $[49] !== launching || $[50] !== outcomes || $[51] !== proposalPubkey || $[52] !== proposalTitle || $[53] !== rewardsPanel || $[54] !== userStake || $[55] !== withdrawFeePercent) {
        t5 = <ProposalForm rewardsPanel={rewardsPanel} proposalPubkey={proposalPubkey} proposalTitle={proposalTitle} outcomes={outcomes} userStake={userStake} withdrawFeePercent={withdrawFeePercent} feeBpsBacking={feeBpsBacking} feeBpsUnbacking={feeBpsUnbacking} bare={true} amountInput={amountInput} onAmountInputChange={setAmountInput} launching={launching} />;
        $[46] = amountInput;
        $[47] = feeBpsBacking;
        $[48] = feeBpsUnbacking;
        $[49] = launching;
        $[50] = outcomes;
        $[51] = proposalPubkey;
        $[52] = proposalTitle;
        $[53] = rewardsPanel;
        $[54] = userStake;
        $[55] = withdrawFeePercent;
        $[56] = t5;
      } else {
        t5 = $[56];
      }
      t4 = t5;
      break bb0;
    }
    let t5;
    if ($[57] !== amountInput || $[58] !== feeBpsBacking || $[59] !== feeBpsUnbacking || $[60] !== launching || $[61] !== outcomes || $[62] !== proposalPubkey || $[63] !== proposalTitle || $[64] !== userStake || $[65] !== withdrawFeePercent) {
      t5 = <Panel density="compact" className="overflow-hidden"><PanelContent><ProposalForm proposalPubkey={proposalPubkey} proposalTitle={proposalTitle} outcomes={outcomes} userStake={userStake} withdrawFeePercent={withdrawFeePercent} feeBpsBacking={feeBpsBacking} feeBpsUnbacking={feeBpsUnbacking} bare={true} amountInput={amountInput} onAmountInputChange={setAmountInput} launching={launching} /></PanelContent></Panel>;
      $[57] = amountInput;
      $[58] = feeBpsBacking;
      $[59] = feeBpsUnbacking;
      $[60] = launching;
      $[61] = outcomes;
      $[62] = proposalPubkey;
      $[63] = proposalTitle;
      $[64] = userStake;
      $[65] = withdrawFeePercent;
      $[66] = t5;
    } else {
      t5 = $[66];
    }
    t4 = t5;
  }
  const statusContent = t4;
  const t5 = bare && isAuthenticated ? null : rewardsPanel;
  let t6;
  if ($[67] !== statusContent || $[68] !== t5) {
    t6 = <div className="flex flex-col gap-4">{statusContent}{t5}</div>;
    $[67] = statusContent;
    $[68] = t5;
    $[69] = t6;
  } else {
    t6 = $[69];
  }
  return t6;
}
interface BackingPhaseRewardsPanelProps {
  proposalPubkey: string;
  amountInput: string;
  currentTotalBackedD6: string;
  viewerBackerShareBalanceD6: string;
  creationThresholdD6: string | undefined;
  showBackingInput: boolean;
  onAmountInputChange: (next: string) => void;
}
function BackingPhaseRewardsPanel(t0) {
  const $ = _c(14);
  const {
    proposalPubkey,
    amountInput,
    currentTotalBackedD6,
    viewerBackerShareBalanceD6,
    creationThresholdD6,
    showBackingInput,
    onAmountInputChange
  } = t0;
  let t1;
  if ($[0] !== proposalPubkey) {
    t1 = {
      pubkey: proposalPubkey
    };
    $[0] = proposalPubkey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const address = useGetProposalField(t1, "marketConfig");
  const {
    feeBpsBuy,
    feeBpsSellNormal,
    feeSplitBackersBps,
    configStatus
  } = useCreationFeeParams(true, address ?? null);
  let t2;
  if ($[2] !== amountInput || $[3] !== configStatus || $[4] !== creationThresholdD6 || $[5] !== currentTotalBackedD6 || $[6] !== feeBpsBuy || $[7] !== feeBpsSellNormal || $[8] !== feeSplitBackersBps || $[9] !== onAmountInputChange || $[10] !== proposalPubkey || $[11] !== showBackingInput || $[12] !== viewerBackerShareBalanceD6) {
    t2 = <ProposalBackerRewardsPanel proposalPubkey={proposalPubkey} amountInput={amountInput} currentTotalBackedD6={currentTotalBackedD6} viewerBackerShareBalanceD6={viewerBackerShareBalanceD6} creationThresholdD6={creationThresholdD6} feeBpsBuy={feeBpsBuy} feeBpsSellNormal={feeBpsSellNormal} feeSplitBackersBps={feeSplitBackersBps} configStatus={configStatus} showBackingInput={showBackingInput} onAmountInputChange={onAmountInputChange} />;
    $[2] = amountInput;
    $[3] = configStatus;
    $[4] = creationThresholdD6;
    $[5] = currentTotalBackedD6;
    $[6] = feeBpsBuy;
    $[7] = feeBpsSellNormal;
    $[8] = feeSplitBackersBps;
    $[9] = onAmountInputChange;
    $[10] = proposalPubkey;
    $[11] = showBackingInput;
    $[12] = viewerBackerShareBalanceD6;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
}